import { datadogLogs } from '@datadog/browser-logs'
import { useAuthToken } from '@shared/stores/authToken.js'
import { useRequest } from '@shared/stores/request'
import { getDate } from '@shared/utils/timeUtils'

const env = import.meta.env

const globalCtx = {
  env: env.VITE_APP_ENVIRONMENT,
  app_name: env.VITE_APP_NAME
}

export function getGlobalCtx() {
  return globalCtx
}

function formatMessage(attrs) {
  let msg = `[${attrs.level.toUpperCase()}] Admin: ${attrs.context.admin_name} – ${String(attrs.context.log_type ?? '')}`
  if (Object.hasOwn(attrs, 'error_info')) {
    msg += ' - ' + attrs.error_info.message
  }
  if (!Object.hasOwn(attrs, 'context')) return msg
  const attrsCtx = { ...attrs.context }
  if (Object.hasOwn(attrsCtx, 'pre_operation_id')) {
    msg += ' - pre_operation_id: ' + attrsCtx.pre_operation_id
  }
  if (Object.hasOwn(attrsCtx, 'pos_operation_id')) {
    msg += ' - pos_operation_id: ' + attrsCtx.pos_operation_id
  }
  if (Object.hasOwn(attrsCtx, 'target_tax_id')) {
    msg += ' - target_tax_id: ' + attrsCtx.target_tax_id
  }
  if (Object.hasOwn(attrsCtx, 'client_id')) {
    msg += ' - client_id: ' + attrsCtx.client_id
  }
  if (Object.hasOwn(attrsCtx, 'edited_admin_id')) {
    msg += ' - edited_admin_id: ' + attrsCtx.edited_admin_id
  }
  return msg
}

export class Logger {
  constructor(name = 'app') {
    this.name = name
  }
  bind(ctx = {}) {
    Object.assign(globalCtx, ctx)
  }
  _emit(level, payload = {}) {
    if (
      !Object.hasOwn(globalCtx, 'institution') ||
      !Object.hasOwn(globalCtx, 'admin_name') ||
      !Object.hasOwn(globalCtx, 'admin_id')
    ) {
      const authentication = useAuthToken()
      log.bind({
        context: {
          institution: authentication.institution,
          admin_name: authentication.name,
          admin_id: authentication.adminId
        }
      })
    }

    const logCtx = { ...payload.context }
    let attrs = {}

    if (logCtx.error != null) {
      attrs.error_info = {
        name: logCtx.error.name,
        message: logCtx.error.message,
        stack: logCtx.error.stack
      }
      delete logCtx.error
    }

    if (useRequest().hasRequestId) {
      attrs.request_id = useRequest().requestId
    }

    attrs = {
      ...attrs,
      ...globalCtx,
      logger_name: payload?.name != null ? payload.name : this.name,
      context: {
        ...globalCtx.context,
        ...logCtx,
        log_type: payload.message
      },
      level: level,
      module: payload.module,
      func_name: payload.func_name,
      timestamp: getDate()
    }

    const msg = formatMessage(attrs)
    attrs.message = msg

    datadogLogs.logger.log(msg, attrs, level !== 'analytics' ? level : 'info')
    if (env.VITE_APP_ENVIRONMENT !== 'prod') {
      let consoleColor
      switch (level) {
        case 'error':
          consoleColor = '#dc3545'
          break
        case 'warning':
          consoleColor = '#ffc107'
          break
        case 'debug':
          consoleColor = '#6c757d'
          break
        case 'analytics':
          consoleColor = '#007bff'
          break
        default:
          consoleColor = '#17a2b8'
      }
      console.log(
        `%c${level}%c  ${msg}`,
        `background: ${consoleColor}; color: white; font-weight: bold; padding: 1px 4px; border-radius: 4px;`,
        'color: inherit; font-weight: normal;',
        {
          ...attrs
        }
      )
    }
    return
  }
  debug(ctx) {
    this._emit('debug', ctx)
  }
  info(ctx) {
    this._emit('info', ctx)
  }
  warn(ctx) {
    this._emit('warning', ctx)
  }
  analytics(ctx) {
    this._emit('analytics', ctx)
  }
  error(ctx) {
    this._emit('error', ctx)
  }
}

export const log = new Logger('app')
